/*
 * Baza de cunoștințe, răsfoită.
 *
 * ---------------------------------------------------------------------------
 * DE CE UN ECRAN, NU DOAR REZULTATE DE CĂUTARE
 * ---------------------------------------------------------------------------
 *
 * Articolele erau accesibile individual — `/help/30` — dar nu exista nicio cale
 * de a AJUNGE la unul fără să-l nimerești dintr-o căutare. Adică existau numai
 * pentru cine ghicea cuvântul potrivit, iar cine voia doar să vadă ce s-a scris
 * nu putea afla.
 *
 * ---------------------------------------------------------------------------
 * FILTRUL DE AICI E ALTUL DECÂT AL CASETEI, ȘI ASTA E INTENȚIONAT
 * ---------------------------------------------------------------------------
 *
 * Caseta răspunde la „găsește-mi răspunsul": ordonează după scor, taie coada,
 * întoarce cel mult patru. Ecranul ăsta răspunde la „arată-mi ce aveți", și
 * atunci un articol sărit fiindcă scorul lui e mic ar arăta ca și cum
 * articolele ar dispărea din listă.
 *
 * Filtrele stau în starea ecranului, nu în adresă — același motiv ca la
 * catalog: la fiecare tastare s-ar schimba ruta, s-ar remonta ecranul și
 * câmpul de căutare și-ar pierde cursorul. Cine vrea să trimită o legătură
 * trimite legătura articolului, care are ruta lui.
 */

import { useEffect, useState } from 'react';
import { config } from '../api/config';
import * as shop from '../api/shop';
import { ArticleCard } from '../components/ArticleCard';
import { Pager } from '../components/Pager';
import { EmptyState, ErrorState, Loading } from '../components/states';
import { t } from '../lib/i18n';
import { useAsync } from '../lib/useAsync';

export function HelpScreen() {
  const [typed, setTyped] = useState('');
  const [query, setQuery] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setQuery(typed.trim());
      setPage(1);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [typed]);

  const categories = useAsync((signal) => shop.helpCategories({ signal }), []);
  const list = useAsync(
    (signal) =>
      shop.helpList(
        { q: query, category_id: categoryId, page, per_page: config.pageSize },
        { signal },
      ),
    [query, categoryId, page],
  );

  const filtered = query !== '' || categoryId !== '';

  return (
    <>
      <header className="page-head">
        <div>
          <p className="micro-label">{t('Help')}</p>
          <h1 className="page-title">{t('Help articles')}</h1>
          <p className="page-sub">{t('Things you can do yourself, without waiting for anybody.')}</p>
        </div>
      </header>

      <div className="toolbar">
        <div className="field field--grow">
          <label className="field__label" htmlFor="help-search">{t('Search')}</label>
          <input
            id="help-search"
            className="field__control"
            type="search"
            placeholder={t('Printer, password, VPN…')}
            value={typed}
            onChange={(event) => setTyped(event.target.value)}
          />
        </div>

        <div className="field">
          <label className="field__label" htmlFor="help-category">{t('Category')}</label>
          <select
            id="help-category"
            className="field__control"
            value={categoryId}
            /* Fără categorii se caută liber. Un filtru care n-a putut fi
               încărcat nu are voie să oprească lista. */
            disabled={categories.status === 'error'}
            onChange={(event) => {
              setCategoryId(event.target.value);
              setPage(1);
            }}
          >
            <option value="">{t('All categories')}</option>
            {(categories.data?.categories ?? []).map((category) => (
              <option key={category.id} value={String(category.id)}>
                {category.name} ({category.count})
              </option>
            ))}
          </select>
        </div>

        <button
          type="button"
          className="btn"
          disabled={!filtered && typed === ''}
          onClick={() => {
            setTyped('');
            setQuery('');
            setCategoryId('');
            setPage(1);
          }}
        >{t('Clear')}</button>
      </div>

      {list.status === 'loading' ? <Loading label={t('Loading articles…')} /> : null}
      {list.status === 'error' && list.error ? (
        <ErrorState error={list.error} onRetry={list.reload} />
      ) : null}

      {list.status === 'ready' && list.data ? (
        list.data.items.length === 0 ? (
          <EmptyState
            title={t('No articles')}
            text={
              filtered
                ? t('Try a different search term, or clear the filters.')
                : t('Nothing has been written for your company yet.')
            }
          />
        ) : (
          <>
            <div className="grid">
              {list.data.items.map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
            <Pager
              total={list.data.total}
              page={list.data.page}
              perPage={list.data.per_page}
              unit={['article', 'articles']}
              onPage={setPage}
            />
          </>
        )
      ) : null}
    </>
  );
}
